import React, { useContext, useState } from "react";
import { AppContext } from "../context/AppContext";
import { BookOpen, CalendarDays, Gauge, Search, Users } from "lucide-react";

const Students: React.FC = () => {
  const ctx = useContext(AppContext);
  const [search, setSearch] = useState("");

  const userClient = ctx.clients.find(
    (client) => client.authId === ctx.user?.uid,
  );

  // Najpierw filtr po szkole, potem po wyszukiwaniu
  const filteredStudents = ctx.students
    .filter((student) => student.schoolToken === userClient?.token)
    .filter((student) => {
      const query = search.toLowerCase();
      return (
        student.name?.toLowerCase().includes(query) ||
        student.surname?.toLowerCase().includes(query)
      );
    });

  const formatDate = (timestamp: number) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString("pl-PL", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  return (
    <div className="mx-auto my-2 max-w-4xl space-y-5">
      <div className="rounded-[24px] border border-white/85 bg-white/5 p-5 shadow-[0_16px_45px_rgba(148,163,184,0.14)] backdrop-blur-md sm:p-6">
        <p className="text-xs mb-2 font-bold uppercase tracking-[0.18em] text-slate-600">
          Wyniki testów z aplikacji mobilnej{" "}
        </p>
        <p className="text-sm leading-7 text-slate-600 mb-4">
          Każdy test zrobiony w aplikacji mobilnej automatycznie znajdzie się w
          tej sekcji. <br></br>
          Twoi uczniowie <b>nie będą widzieć</b> sugerowanego dla nich poziomu
          książki.
        </p>
        <div className="grid gap-5 md:grid-cols-2">
          <div className="rounded-[24px] border border-white/50 bg-white/5 shadow-[0_16px_45px_rgba(148,163,184,0.14)] backdrop-blur-md">
            <div className="border-b border-slate-200/50 px-5 py-4">
              <p className="text-lg font-semibold text-slate-900">
                Co widzi szkoła?
              </p>
            </div>

            <div className="px-5 py-4">
              <div className="space-y-3">
                <div className="grid grid-cols-[1fr_auto] items-start gap-x-4 gap-y-1.5 rounded-[18px] border border-slate-200/60 bg-white/60 p-3.5">
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <CalendarDays
                      size={16}
                      strokeWidth={2.2}
                      className="shrink-0 text-slate-400"
                    />
                    01.01.2027
                  </div>

                  <div className="flex items-center gap-2 text-sm font-semibold text-slate-800">
                    <Gauge
                      size={16}
                      strokeWidth={2.2}
                      className="shrink-0 text-slate-400"
                    />
                    37/40
                  </div>

                  <div className="col-span-2 flex items-center gap-2 text-sm text-slate-600">
                    <BookOpen
                      size={16}
                      strokeWidth={2.2}
                      className="shrink-0 text-slate-400"
                    />
                    Poziom: English Unlocked 7
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="rounded-[24px] border border-white/50 bg-white/5 shadow-[0_16px_45px_rgba(148,163,184,0.14)] backdrop-blur-md">
            <div className="border-b border-slate-200/50 px-5 py-4">
              <p className="text-lg font-semibold text-slate-900">
                Co widzi uczeń w swoim telefonie?
              </p>
            </div>

            <div className="px-5 py-4">
              <div className="space-y-3">
                <div className="grid grid-cols-[1fr_auto] items-start gap-x-4 gap-y-1.5 rounded-[18px] border border-slate-200/60 bg-white/60 p-3.5">
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <CalendarDays
                      size={16}
                      strokeWidth={2.2}
                      className="shrink-0 text-slate-400"
                    />
                    01.01.2027
                  </div>

                  <div className="flex items-center gap-2 text-sm font-semibold text-slate-800">
                    <Gauge
                      size={16}
                      strokeWidth={2.2}
                      className="shrink-0 text-slate-400"
                    />
                    37/40
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="rounded-[24px] border border-white/50 bg-white/5 p-5 shadow-[0_16px_45px_rgba(148,163,184,0.14)] backdrop-blur-md sm:p-6">
        <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-600 mb-4">
          Szukaj
        </p>

        <div className="relative">
          <Search
            size={20}
            strokeWidth={2.2}
            className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
          />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Szukaj po imieniu lub nazwisku..."
            className="w-full rounded-2xl border border-slate-200 bg-white/50 py-3 pl-12 pr-4 text-slate-800 outline-none transition focus:border-orange-300 focus:ring-4 focus:ring-orange-100"
          />
        </div>
      </div>

      {filteredStudents.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-[24px] border border-white/50 bg-white/5 px-6 py-14 text-center shadow-[0_16px_45px_rgba(148,163,184,0.14)] backdrop-blur-md">
          <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 text-slate-300">
            <Users size={22} strokeWidth={1.8} />
          </div>
          <p className="max-w-xs text-sm text-slate-500">
            Tutaj wyświetlą się uczniowie, którzy zalogowali się i poprawnie
            wpisali token szkoły w aplikacji mobilnej.
          </p>
        </div>
      ) : (
        <div className="grid gap-5 md:grid-cols-2">
          {filteredStudents.map((student) => (
            <div
              key={student.id}
              className="rounded-[24px] border border-white/50 bg-white/5 shadow-[0_16px_45px_rgba(148,163,184,0.14)] backdrop-blur-md"
            >
              {/* Student name — separated as a header band */}
              <div className="border-b border-slate-200/50 px-5 py-4">
                <p className="text-lg font-semibold text-slate-900">
                  {student?.name} {student?.surname}
                </p>
              </div>

              {/* Test results */}
              <div className="px-5 py-4">
                {student.placementTests?.length > 0 ? (
                  <div className="space-y-3">
                    {student.placementTests
                      .sort((a, b) => b.date - a.date)
                      .map((test, idx) => (
                        <div
                          key={idx}
                          className="grid grid-cols-[1fr_auto] items-start gap-x-4 gap-y-1.5 rounded-[18px] border border-slate-200/60 bg-white/60 p-3.5"
                        >
                          <div className="flex items-center gap-2 text-sm text-slate-600">
                            <CalendarDays
                              size={16}
                              strokeWidth={2.2}
                              className="shrink-0 text-slate-400"
                            />
                            {formatDate(test.date)}
                          </div>
                          <div className="flex items-center gap-2 text-sm font-semibold text-slate-800">
                            <Gauge
                              size={16}
                              strokeWidth={2.2}
                              className="shrink-0 text-slate-400"
                            />
                            {test.score}/40
                          </div>
                          <div className="col-span-2 flex items-center gap-2 text-sm text-slate-600">
                            <BookOpen
                              size={16}
                              strokeWidth={2.2}
                              className="shrink-0 text-slate-400"
                            />
                            Poziom: English Unlocked {test.bookNum}
                          </div>
                        </div>
                      ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-500">
                    Brak testów poziomujących.
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Students;
